/**
 * One retry for a subscription that was refused before it had a right to be.
 *
 * ── The bug this exists for ───────────────────────────────────────────────
 *
 * 22 Aug 2026, watching a brand-new account sign up on staging: the moment
 * `onUser` fired, every subscription the app opens — theme, brand, appearance,
 * commission split, the trip index, the trip list, the leads badge, billing —
 * was refused at once with `permission-denied`. Ten failures in the same
 * second. A reload fixed it completely.
 *
 * The rules were not the problem: they were current, deployed, and grant an
 * owner read on their own documents with no further conditions. The problem is
 * that the reads went out before the freshly-created account's ID token was
 * usable, so Firestore evaluated them as nobody.
 *
 * What made it worth fixing rather than noting is WHO sees it. It happens on
 * the first run of a new account and only then — which means the first thing a
 * brand-new advisor sees, one second after signing up, is a full-width error
 * saying their trips could not be loaded. They have no reason to guess that
 * reloading fixes it, and every reason to conclude the product is broken.
 *
 * ── Why a retry, and not only the token wait ──────────────────────────────
 *
 * `main.js` now awaits `user.getIdToken()` before it starts anything, which
 * addresses the cause as diagnosed. This is the belt to that pair of braces:
 * it heals a first-read denial whatever produced it — the token, the listen
 * stream re-authenticating, a claim that has not propagated — instead of only
 * the one mechanism I was able to observe.
 *
 * ── What it deliberately does NOT do ──────────────────────────────────────
 *
 * It retries ONCE. A subscription that is genuinely refused — a locked
 * account, a path this advisor does not own, a rule that really says no — must
 * still fail, visibly, and quickly. Retrying such a read forever would turn a
 * clear "you cannot see this" into a screen that never settles, and would bill
 * the advisor a document read every time it tried.
 *
 * It also only retries the two codes that can mean "not yet". Anything else —
 * `unavailable`, `not-found`, a malformed query — is reported the first time.
 */

/** How long to wait before trying again. Long enough for a token exchange. */
export const RETRY_DELAY_MS = 700

/**
 * Is this the kind of refusal that might not be a refusal?
 *
 * `unauthenticated` is the honest version of this state. `permission-denied`
 * is what Firestore actually returns when a rule evaluates against no auth,
 * which is why it is here despite also being the code for a real denial —
 * hence exactly one retry, and no more.
 *
 * @param {any} err
 * @returns {boolean}
 */
export function isMaybeNotYet(err) {
  const code = err && err.code
  return code === 'permission-denied' || code === 'unauthenticated'
}

/**
 * Start a subscription, and give it one more chance if its first answer was a
 * refusal that might just have been early.
 *
 * @param {(onError: (err: any) => void) => (() => void)} start - opens the
 *   subscription and returns its unsubscribe
 * @param {(err: any) => void} [onError] - called only once the retry is spent
 * @param {{delayMs?: number, wait?: (fn: () => void, ms: number) => any,
 *   cancel?: (id: any) => void}} [options]
 * @returns {() => void} unsubscribe
 */
export function watchWithRetry(start, onError, options = {}) {
  const delayMs = options.delayMs == null ? RETRY_DELAY_MS : options.delayMs
  const wait = options.wait || ((fn, ms) => setTimeout(fn, ms))
  const cancel = options.cancel || ((id) => clearTimeout(id))

  let stop = /** @type {() => void} */ (() => {})
  let timer = /** @type {any} */ (null)
  let retried = false
  /* The caller can unsubscribe while the retry is still pending. Without this
     the timer would fire and open a subscription nobody is listening to, on a
     screen that has already gone. */
  let released = false

  function begin() {
    stop = start((err) => {
      if (released) return
      if (!retried && isMaybeNotYet(err)) {
        retried = true
        /* Close the refused stream before opening another, or the failed one
           keeps its own retry schedule and both stay alive. */
        stop()
        timer = wait(() => {
          if (!released) begin()
        }, delayMs)
        return
      }
      if (onError) onError(err)
    })
  }

  begin()

  return () => {
    released = true
    if (timer) cancel(timer)
    stop()
  }
}
